import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';

/**
 * Auth gate for any /portal-restricted simulator.
 * Reads localStorage keys set by UserPortalLoginPage:
 *   - practiq_portal_email
 *   - practiq_portal_token
 *
 * If either is missing, shows a sleek lock screen with a "Sign in to continue" CTA
 * that redirects to /portal/login?redirect=<current-path>.
 *
 * On successful login the existing flow lands the user on /portal — to make the
 * redirect work end-to-end we let UserPortalLoginPage honour the ?redirect query.
 */
const RequirePortalLogin = ({ children, label = 'this simulator' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (typeof window !== 'undefined' ? localStorage.getItem('practiq_portal_email') : '') || '';
  const token = (typeof window !== 'undefined' ? localStorage.getItem('practiq_portal_token') : '') || '';

  if (email && token) return children;

  const next = encodeURIComponent(location.pathname + (location.search || ''));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] px-6">
      <div
        className="max-w-md w-full bg-[#11121f]/80 backdrop-blur-md rounded-2xl border border-white/10 p-8 text-center shadow-2xl"
        data-testid="portal-login-gate"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-blue-500/15 border border-blue-400/30 flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-blue-300" />
        </div>
        <h2 className="text-white text-xl font-semibold mb-2">Sign in to continue</h2>
        <p className="text-white/60 text-sm mb-6 leading-relaxed">
          Please log in to your PractiQ account to access {label}. Your practice history will be saved
          automatically so you can pick up where you left off.
        </p>
        <button
          onClick={() => navigate(`/portal/login?redirect=${next}`)}
          data-testid="portal-login-cta"
          className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium hover:from-blue-400 hover:to-indigo-400 transition-colors shadow-lg"
        >
          Sign in to PractiQ
        </button>
        <button
          onClick={() => navigate('/')}
          className="mt-3 text-white/45 hover:text-white text-xs"
        >
          Back to home
        </button>
      </div>
    </div>
  );
};

export default RequirePortalLogin;
